import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../../lib/api'
import { getToken } from '../../lib/auth'
import { useAuthSession } from '../../lib/authSession'
import BookRecordComparison from './BookRecordComparison'
import {
  bookTwinConflicts,
  buildBookExecuteRequest,
  buildBookPreviewRequest,
  classifyBookMutationError,
  displayReview,
  editsTradeDate,
  finalTermsChoiceText,
  isBookAction,
  mutationFromBookReceipt,
  stableBookIdempotencyKey,
  twinDistinctAnswer,
  type BookDateCitationInput,
  type BookTwinAnswer,
  type BookFieldEdits,
  type BookMutationState,
  type BookServerAction,
} from './bookActionController'
import type { BookFieldView, BookObjectView, BookReview, BookReviewAction } from './bookReview'
import type { BookReviewScope } from './bookReviewQuery'
import {
  executeBookAction,
  fetchBookActionReceipt,
  mapBookActions,
  previewBookActions,
  type BookActionReceipt,
} from './bookReviewApi'
import './book-action-controller.css'

export interface BookPlugInput {
  unitsDelta?: string
  date?: string
  note?: string
}

export interface BookActionControllerProps {
  groupId: string
  review: BookReview
  actions: readonly BookServerAction[]
  /** Captured by the read response; never refreshed at click time. */
  deckVersion: string | null
  /** The same admitted fund/client scope used by the GET review. */
  scope?: BookReviewScope
  taskId?: string | null
  onRefresh?: () => void
  /** Refresh counts while the parent retains this actual applied receipt. */
  onApplied?: () => void
  onPendingChange?: (pending: boolean) => void
  onBookPlug?: (input: BookPlugInput) => Promise<unknown>
}

interface EligibilityPreview {
  key: string
  session: string | null
  actions: readonly BookServerAction[]
}

const idleMutation: BookMutationState = { status: 'idle', outcomes: [], message: null }

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') {
      if (detail === 'draft_correction_physical_facts_unavailable')
        return 'The final date or amount could not be verified in this document. Check the cited source.'
      if (detail.startsWith('draft_correction_') || detail.startsWith('final_terms_'))
        return 'The document or provisional trade no longer matches this decision. Refresh the review and check the final terms.'
      return detail
    }
    const message = (detail as { message?: unknown } | undefined)?.message
    if (typeof message === 'string' && message) return message
    return error.status + ' ' + error.statusText
  }
  return error instanceof Error ? error.message : 'The Book decision could not be submitted.'
}

function receiptOutcomes(receipt: BookActionReceipt): BookMutationState {
  return mutationFromBookReceipt(receipt)
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

export default function BookActionController({
  groupId,
  review,
  actions,
  deckVersion,
  scope = {},
  taskId,
  onRefresh,
  onApplied,
  onPendingChange,
  onBookPlug,
}: BookActionControllerProps) {
  const [edits, setEdits] = useState<Record<string, BookFieldEdits>>({})
  const [dateCitations, setDateCitations] = useState<Record<string, BookDateCitationInput>>({})
  const [finalTerms, setFinalTerms] = useState<{
    context: number
    deck: string | null | undefined
    ids: string[]
  }>({ context: -1, deck: null, ids: [] })
  const [nominalReview, setNominalReview] = useState<{
    context: number
    deck: string | null | undefined
    ids: string[]
  }>({ context: -1, deck: null, ids: [] })
  const [mutation, setMutation] = useState<BookMutationState>(idleMutation)
  // Twin-guard 409 escape hatch restored on the Book surface (was RkCard's).
  // Only the server's structured answer arms it; one explicit click re-submits
  // the same selection with the exact booked twins the server named.
  const [twinBlocked, setTwinBlocked] = useState<{
    actionIds: readonly string[]
    pendingId: string
    answer: Record<string, BookTwinAnswer>
  } | null>(null)
  const [pendingActionId, setPendingActionId] = useState<string | null>(null)
  const [plugInput, setPlugInput] = useState<BookPlugInput>({})
  const [plugPending, setPlugPending] = useState(false)
  const mounted = useRef(true)
  const requestSerial = useRef(0)
  const pollInFlight = useRef(false)
  const appliedCallback = useRef(onApplied)
  appliedCallback.current = onApplied
  const [receiptRequestId, setReceiptRequestId] = useState<string | null>(null)
  const authSession = useAuthSession()
  const clientId = scope.clientId ?? null
  const fundId = scope.fundId ?? null
  const reviewKey = JSON.stringify([groupId, review.binding.value, clientId, fundId])
  const displayedContext = useRef({ key: reviewKey, session: authSession, version: 0 })
  if (
    displayedContext.current.key !== reviewKey ||
    displayedContext.current.session !== authSession
  ) {
    displayedContext.current = {
      key: reviewKey,
      session: authSession,
      version: displayedContext.current.version + 1,
    }
  }
  const finalTermsIds =
    finalTerms.context === displayedContext.current.version && finalTerms.deck === deckVersion
      ? finalTerms.ids
      : []
  const contextVersion = displayedContext.current.version
  const nominalReviewIds = useMemo(
    () =>
      nominalReview.context === contextVersion && nominalReview.deck === deckVersion
        ? nominalReview.ids
        : [],
    [nominalReview, deckVersion, contextVersion]
  )
  const [preview, setPreview] = useState<EligibilityPreview | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const previewAbort = useRef<AbortController | null>(null)
  const previewSerial = useRef(0)
  const executionPending = useRef(false)
  const executionContext = useRef<{
    groupId: string
    session: string | null
    clientId: string | null
    fundId: string | null
  } | null>(null)
  const hasEdits = Object.values(edits).some((fields) => Object.keys(fields).length > 0)
  const hasPreviewIntent = hasEdits || nominalReviewIds.length > 0
  const previewKey = JSON.stringify([
    groupId,
    review.binding.value,
    deckVersion,
    scope.clientId ?? null,
    scope.fundId ?? null,
    actions,
    edits,
    nominalReviewIds,
  ])
  const livePreview = useRef({ key: previewKey, session: authSession })
  livePreview.current = { key: previewKey, session: authSession }
  const activeActions = useMemo(() => {
    if (!hasPreviewIntent) return actions
    if (preview?.key === previewKey && preview.session === authSession) return preview.actions
    // Local drafts never grant permission. Only the server's complete
    // effective preview may re-enable an edited selection.
    return actions.map((action) => ({ ...action, disabled: true }))
  }, [actions, authSession, hasPreviewIntent, preview, previewKey])
  const actionsLocked =
    mutation.status === 'submitting' ||
    mutation.status === 'pending' ||
    mutation.status === 'applied' ||
    receiptRequestId !== null
  const shownReview = useMemo(() => {
    const displayed = displayReview(review, activeActions, pendingActionId)
    if (!actionsLocked) return displayed
    return {
      ...displayed,
      actions: displayed.actions?.map((action) => ({ ...action, disabled: true })),
    }
  }, [activeActions, actionsLocked, pendingActionId, review])
  const blocked = activeActions.filter((action) => !isBookAction(action) && action.disabled_reason)
  const plugAction = actions.find((action) => action.kind === 'plug' || action.action === 'plug')

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      previewSerial.current += 1
      previewAbort.current?.abort()
    }
  }, [])

  useEffect(() => {
    const running = executionContext.current
    const sameExecution =
      executionPending.current &&
      running?.groupId === groupId &&
      running.session === authSession &&
      running.clientId === clientId &&
      running.fundId === fundId
    // A refreshed read must not lose an in-flight execution/receipt for this
    // same scope or restart previews while that decision is still pending.
    if (!sameExecution) {
      requestSerial.current += 1
      setPendingActionId(null)
      setMutation(idleMutation)
      setTwinBlocked(null)
      setReceiptRequestId(null)
      executionPending.current = false
      executionContext.current = null
    }
    setEdits({})
    setDateCitations({})
    setFinalTerms({ context: -1, deck: null, ids: [] })
    setNominalReview({ context: -1, deck: null, ids: [] })
    setPreview(null)
    setPreviewError(null)
    previewSerial.current += 1
    previewAbort.current?.abort()
  }, [authSession, groupId, review.binding.value, clientId, fundId])

  useEffect(() => {
    previewAbort.current?.abort()
    const serial = ++previewSerial.current
    if (!hasPreviewIntent || actionsLocked || plugPending || !deckVersion || !authSession) {
      setPreviewBusy(false)
      return
    }
    const controller = new AbortController()
    previewAbort.current = controller
    const key = previewKey
    const session = authSession
    setPreviewBusy(true)
    setPreviewError(null)
    const current = () =>
      mounted.current &&
      !controller.signal.aborted &&
      previewSerial.current === serial &&
      livePreview.current.key === key &&
      livePreview.current.session === session &&
      getToken() === session &&
      !executionPending.current
    const timer = window.setTimeout(async () => {
      const body = buildBookPreviewRequest(
        review,
        deckVersion,
        { clientId, fundId },
        edits,
        nominalReviewIds
      )
      try {
        const result = await previewBookActions(groupId, body, controller.signal)
        if (!current()) return
        if (
          result.group_id !== groupId ||
          result.deck_version !== deckVersion ||
          result.review_binding_value !== review.binding.value ||
          JSON.stringify([...result.presented_proposal_ids].sort()) !==
            JSON.stringify([...body.presented_proposal_ids].sort())
        )
          throw new Error('The review changed. Refresh it before booking.')
        setPreview({ key, session, actions: mapBookActions(result) })
      } catch (error) {
        if (!current()) return
        setPreview(null)
        setPreviewError(errorMessage(error))
      } finally {
        if (current()) setPreviewBusy(false)
      }
    }, 300)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [
    actionsLocked,
    authSession,
    deckVersion,
    edits,
    groupId,
    hasPreviewIntent,
    nominalReviewIds,
    plugPending,
    previewKey,
    review,
    clientId,
    fundId,
  ])

  // Booking re-verifies every source before it writes, which takes ~20s on a
  // large group (option B, Ale 14 Oct): poll fast through that window, then
  // gently for a further two minutes, so the officer watches progress instead
  // of landing on a dead "still pending" card (Ale 1 Nov: the wait must look
  // like work, not a freeze).
  const RECEIPT_WAITS_MS: readonly number[] = [
    ...Array<number>(60).fill(500),
    ...Array<number>(60).fill(2000),
  ]

  const pollReceipt = async (requestId: string, serial: number) => {
    // One receipt loop at a time: a "Check status" click while the automatic
    // loop still runs must not start a second loop — its timeout tail would
    // later overwrite an applied card with a false "still booking".
    if (pollInFlight.current) return
    pollInFlight.current = true
    try {
      await pollReceiptLoop(requestId, serial)
    } finally {
      pollInFlight.current = false
    }
  }

  const pollReceiptLoop = async (requestId: string, serial: number) => {
    const lastAttempt = RECEIPT_WAITS_MS.length - 1
    for (let attempt = 0; attempt <= lastAttempt; attempt += 1) {
      if (!mounted.current || requestSerial.current !== serial) return
      try {
        const receipt = await fetchBookActionReceipt(groupId, requestId)
        if (!mounted.current || requestSerial.current !== serial) return
        if (receipt.state === 'applied') {
          setMutation(receiptOutcomes(receipt))
          setReceiptRequestId(null)
          onPendingChange?.(false)
          // Count refresh does not discard the actual receipt in adopted parents.
          // Legacy parents retain their existing terminal refresh behavior.
          if (appliedCallback.current) appliedCallback.current()
          else onRefresh?.()
          return
        }
        if (
          receipt.state === 'refused' ||
          receipt.state === 'conflicted' ||
          receipt.state === 'deferred'
        ) {
          setMutation(receiptOutcomes(receipt))
          if (
            receipt.state === 'refused' ||
            receipt.state === 'conflicted' ||
            receipt.state === 'deferred'
          ) {
            executionPending.current = false
            setReceiptRequestId(null)
            setPendingActionId(null)
            onPendingChange?.(false)
          }
          return
        }
        setMutation(receiptOutcomes(receipt))
      } catch {
        if (attempt === lastAttempt && mounted.current && requestSerial.current === serial) {
          setMutation({
            status: 'pending',
            outcomes: [],
            message: 'Submitted — receipt check failed; use Check status to retry.',
          })
          return
        }
      }
      if (attempt === 59 && mounted.current && requestSerial.current === serial) {
        setMutation((current) =>
          current.status === 'pending'
            ? {
                ...current,
                message: 'Still booking — re-verification is taking longer than usual…',
              }
            : current
        )
      }
      await sleep(RECEIPT_WAITS_MS[attempt])
    }
    if (mounted.current && requestSerial.current === serial) {
      setMutation({
        status: 'pending',
        outcomes: [],
        message: 'Still booking — you can keep working; Check status refreshes this card.',
      })
    }
  }

  const submitPlans = async (
    actionIds: readonly string[],
    pendingId: string,
    twinDistinct?: Record<string, BookTwinAnswer>
  ) => {
    if (
      mutation.status === 'submitting' ||
      mutation.status === 'pending' ||
      receiptRequestId !== null ||
      !deckVersion
    )
      return
    if (executionPending.current || !authSession || getToken() !== authSession) return
    const request = buildBookExecuteRequest(
      actionIds,
      activeActions,
      review,
      deckVersion,
      scope,
      edits,
      finalTermsIds,
      nominalReviewIds,
      dateCitations
    )
    if (!request.proposal_ids.length) return
    if (twinDistinct) {
      if (Object.keys(twinDistinct).some((id) => !request.proposal_ids.includes(id))) return
      request.twin_distinct = twinDistinct
    }
    setTwinBlocked(null)
    executionPending.current = true
    executionContext.current = { groupId, session: authSession, clientId, fundId }
    setPreviewError(null)
    previewSerial.current += 1
    previewAbort.current?.abort()
    const serial = requestSerial.current + 1
    requestSerial.current = serial
    setPendingActionId(pendingId)
    setMutation({ status: 'submitting', outcomes: [], message: null })
    onPendingChange?.(true)
    try {
      const accepted = await executeBookAction(
        groupId,
        request,
        stableBookIdempotencyKey(groupId, pendingId, request)
      )
      if (!mounted.current || requestSerial.current !== serial) return
      setReceiptRequestId(accepted.request_id)
      setMutation({
        status: 'pending',
        outcomes: accepted.proposal_ids.map((proposal_id) => ({
          proposal_id,
          status: accepted.state,
        })),
        message: 'Booking — re-verifying every source against the record (usually ~20 s)…',
      })
      await pollReceipt(accepted.request_id, serial)
    } catch (error) {
      if (!mounted.current || requestSerial.current !== serial) return
      executionPending.current = false
      setPendingActionId(null)
      setReceiptRequestId(null)
      onPendingChange?.(false)
      const conflicts = bookTwinConflicts(error)
      const answer = conflicts && !twinDistinct ? twinDistinctAnswer(conflicts) : null
      if (answer) setTwinBlocked({ actionIds, pendingId, answer })
      const status = conflicts ? 'refused' : classifyBookMutationError(error)
      setMutation({ status, outcomes: [], message: errorMessage(error) })
    }
  }

  const toggleDisplayAction = (action: BookReviewAction) => {
    if (actionKindForController(action) === 'book_all') {
      const plan = activeActions.find((candidate) => candidate.id === action.id)
      if (plan && actionKindForController(plan) === 'book_all' && !plan.disabled)
        void submitPlans([plan.id], plan.id)
      return
    }
    const plan = activeActions.find((candidate) => candidate.id === action.id)
    if (plan && isBookAction(plan)) void submitPlans([plan.id], plan.id)
  }

  const onFieldChange = (object: BookObjectView, field: BookFieldView, value: unknown) => {
    if (actionsLocked || plugPending || executionPending.current) return
    previewSerial.current += 1
    previewAbort.current?.abort()
    const key = field.proposalKey
    if (!key) return
    setTwinBlocked(null)
    setFinalTerms((current) => ({
      ...current,
      ids: current.ids.filter((id) => id !== object.proposal_id),
    }))
    setNominalReview((current) => ({
      ...current,
      ids: current.ids.filter((id) => id !== object.proposal_id),
    }))
    setEdits((current) => ({
      ...current,
      [object.proposal_id]: {
        ...(current[object.proposal_id] ?? {}),
        [key]: value,
      },
    }))
  }

  const submitPlug = async () => {
    if (!taskId || !onBookPlug || plugPending || actionsLocked || executionPending.current) return
    executionPending.current = true
    previewSerial.current += 1
    previewAbort.current?.abort()
    setPreviewError(null)
    setPlugPending(true)
    setPendingActionId(plugAction?.id ?? null)
    setMutation({ status: 'submitting', outcomes: [], message: null })
    try {
      await onBookPlug(plugInput)
      setMutation({
        status: 'pending',
        outcomes: [],
        message: 'Booking — re-verifying every source against the record (usually ~20 s)…',
      })
    } catch (error) {
      executionPending.current = false
      setPendingActionId(null)
      const status = classifyBookMutationError(error)
      setMutation({ status, outcomes: [], message: errorMessage(error) })
    } finally {
      setPlugPending(false)
    }
  }

  return (
    <section className="book-action-controller" data-testid="book-action-controller">
      <BookRecordComparison
        key={displayedContext.current.version}
        review={shownReview}
        pendingActionId={pendingActionId}
        onAction={toggleDisplayAction}
        onFieldChange={onFieldChange}
      />
      {review.objects
        .filter((object) => object.role !== 'effect' && editsTradeDate(object, edits))
        .map((object) => {
          const citation = dateCitations[object.proposal_id] ?? { page: '', text: '' }
          const change = (patch: Partial<BookDateCitationInput>) => {
            if (actionsLocked || plugPending || executionPending.current) return
            setTwinBlocked(null) // a changed intent never inherits a twin answer
            setDateCitations((current) => ({
              ...current,
              [object.proposal_id]: { ...citation, ...patch },
            }))
          }
          return (
            <fieldset
              key={object.proposal_id}
              className="book-action-controller__controls"
              data-testid="book-date-citation"
            >
              <legend>{object.title || 'Trade'}: where does the document state this date?</legend>
              <p>
                To correct a date the document already states, give the page and copy the exact
                sentence. The date is booked only if that sentence is found on that page and states
                it.
              </p>
              <label>
                Page
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={citation.page}
                  disabled={actionsLocked || plugPending}
                  onChange={(event) => change({ page: event.target.value })}
                />
              </label>
              <label>
                Exact sentence from the document
                <textarea
                  rows={2}
                  maxLength={500}
                  value={citation.text}
                  disabled={actionsLocked || plugPending}
                  onChange={(event) => change({ text: event.target.value })}
                />
              </label>
            </fieldset>
          )
        })}
      {review.objects
        .filter((object) => object.role !== 'effect' && object.final_terms_review)
        .map((object) => (
          <label
            key={object.proposal_id}
            className="book-action-controller__controls"
            data-testid="book-final-terms-choice"
          >
            <input
              type="checkbox"
              checked={finalTermsIds.includes(object.proposal_id)}
              disabled={
                actionsLocked ||
                plugPending ||
                Object.keys(edits[object.proposal_id] ?? {}).length > 0
              }
              onChange={(event) => {
                if (actionsLocked || plugPending || executionPending.current) return
                setTwinBlocked(null)
                setFinalTerms({
                  context: displayedContext.current.version,
                  deck: deckVersion,
                  ids: event.target.checked
                    ? [...new Set([...finalTermsIds, object.proposal_id])]
                    : finalTermsIds.filter((id) => id !== object.proposal_id),
                })
              }}
            />
            <span>{finalTermsChoiceText(object)}</span>
          </label>
        ))}
      {review.objects
        .filter(
          (object) =>
            object.role !== 'effect' &&
            object.nominal_citation_review &&
            object.proposal_status === 'pending'
        )
        .map((object) => (
          <label
            key={object.proposal_id}
            className="book-action-controller__controls"
            data-testid="book-nominal-review-choice"
          >
            <input
              type="checkbox"
              checked={nominalReviewIds.includes(object.proposal_id)}
              disabled={
                actionsLocked ||
                plugPending ||
                Object.keys(edits[object.proposal_id] ?? {}).length > 0
              }
              onChange={(event) => {
                if (actionsLocked || plugPending || executionPending.current) return
                previewSerial.current += 1
                previewAbort.current?.abort()
                setTwinBlocked(null)
                setNominalReview({
                  context: displayedContext.current.version,
                  deck: deckVersion,
                  ids: event.target.checked
                    ? [...new Set([...nominalReviewIds, object.proposal_id])]
                    : nominalReviewIds.filter((id) => id !== object.proposal_id),
                })
              }}
            />
            <span>
              {object.title || 'Trade'}: I checked the original document and choose to book this
              trade as stated despite the unverified nominal citation. This choice cannot include
              trade edits or other unverified source details.
            </span>
          </label>
        ))}
      {previewBusy ? <p role="status">Checking changes…</p> : null}
      {previewError ? (
        <div role="alert">
          <p>{previewError}</p>
          {onRefresh ? (
            <button type="button" onClick={onRefresh}>
              Refresh review
            </button>
          ) : null}
        </div>
      ) : null}
      {blocked.length ? (
        <div className="book-action-controller__blocked" data-testid="book-blocked-actions">
          {blocked.map((action) => (
            <p key={action.id}>
              {action.label}: {action.disabled_reason}
            </p>
          ))}
        </div>
      ) : null}
      {(mutation.status === 'stale' ||
        mutation.status === 'refused' ||
        mutation.status === 'applied') &&
      onRefresh ? (
        <div className="book-action-controller__controls">
          <button type="button" onClick={onRefresh}>
            Refresh review
          </button>
        </div>
      ) : null}
      {mutation.status === 'pending' && receiptRequestId ? (
        <div className="book-action-controller__controls">
          <button
            type="button"
            onClick={() => void pollReceipt(receiptRequestId, requestSerial.current)}
          >
            Check status
          </button>
        </div>
      ) : null}
      {mutation.message ? (
        <p
          className={
            'book-action-controller__mutation book-action-controller__mutation--' + mutation.status
          }
          role={mutation.status === 'error' || mutation.status === 'stale' ? 'alert' : 'status'}
        >
          {mutation.message}
        </p>
      ) : null}
      {twinBlocked && mutation.status === 'refused' ? (
        <div className="book-action-controller__controls">
          <button
            type="button"
            data-testid="twin-book-anyway"
            disabled={actionsLocked || plugPending}
            onClick={() =>
              void submitPlans(twinBlocked.actionIds, twinBlocked.pendingId, twinBlocked.answer)
            }
          >
            Book anyway — this is a distinct event
          </button>
        </div>
      ) : null}
      {plugAction ? (
        <section className="book-action-controller__plug" data-testid="book-plug-action">
          <h3>{plugAction.label}</h3>
          <p>
            {plugAction.disabled_reason ??
              'This provisional movement remains separate from document approval.'}
          </p>
          <label>
            Amount
            <input
              disabled={plugPending || actionsLocked || executionPending.current}
              value={plugInput.unitsDelta ?? ''}
              onChange={(event) =>
                setPlugInput((current) => ({ ...current, unitsDelta: event.target.value }))
              }
            />
          </label>
          <label>
            Date (optional)
            <input
              type="date"
              disabled={plugPending || actionsLocked || executionPending.current}
              value={plugInput.date ?? ''}
              onChange={(event) =>
                setPlugInput((current) => ({ ...current, date: event.target.value }))
              }
            />
          </label>
          <button
            type="button"
            disabled={
              !onBookPlug ||
              plugPending ||
              actionsLocked ||
              executionPending.current ||
              plugAction.disabled
            }
            onClick={() => void submitPlug()}
          >
            {plugPending ? 'Submitting…' : 'Book provisional movement'}
          </button>
        </section>
      ) : null}
    </section>
  )
}

function actionKindForController(action: { kind?: string; action?: string }): string {
  return action.kind ?? action.action ?? 'book'
}
